import {
  ChevronDown,
  ChevronUp,
  Crosshair,
  Play,
  Plus,
  Repeat,
  SlidersHorizontal,
  Sword,
  X,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import {
  addToEncounter,
  createEncounter,
  endEncounter,
  nextTurn,
  removeFromEncounter,
  startEncounter,
} from "../state/graph-actions";
import {
  type EncounterTurnEntry,
  useBlockSummaries,
  useCurrentSceneId,
  useEncounterForScene,
} from "../state/graph-hooks";
import { useYDoc } from "../state/yjs-provider";

function toFiniteNumber(value: unknown): number {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
}

export function TurnTracker() {
  const doc = useYDoc();
  const sceneId = useCurrentSceneId();
  const encounter = useEncounterForScene(sceneId);
  const blockSummaries = useBlockSummaries();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [selectedParticipantId, setSelectedParticipantId] = useState("");

  const combatantChoices = useMemo(
    () =>
      blockSummaries.filter(
        (block) => block.type === "token" || block.type === "npc",
      ),
    [blockSummaries],
  );

  const encounterParticipants = encounter?.turnOrder ?? [];
  const participantSet = useMemo(
    () => new Set(encounterParticipants.map((entry) => entry.blockId)),
    [encounterParticipants],
  );

  const availableParticipants = useMemo(
    () => combatantChoices.filter((entry) => !participantSet.has(entry.id)),
    [combatantChoices, participantSet],
  );

  useEffect(() => {
    if (!encounter) {
      setIsCollapsed(false);
      return;
    }

    if (encounter.status === "ended") {
      setIsCollapsed(true);
    } else {
      setIsCollapsed(false);
    }
  }, [encounter?.id, encounter?.status]);

  if (!encounter) {
    return (
      <div className="pointer-events-auto rounded border border-white/12 bg-black/45 px-3 py-2 text-[11px] text-white/85 backdrop-blur">
        <div className="flex items-center gap-2">
          <Sword className="h-4 w-4 text-amber-300" />
          <span>No active encounter in scene.</span>
          <button
            type="button"
            className="ml-auto rounded px-2 py-1 bg-white/10 border border-white/20 text-[10px] hover:bg-white/15"
            onClick={() => {
              if (!sceneId) return;
              createEncounter(doc, sceneId);
            }}
          >
            Create Encounter
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="pointer-events-auto w-[min(84vw,900px)] rounded border border-white/12 bg-black/45 px-3 py-2 text-[11px] text-white/85 backdrop-blur">
      <div className="flex items-center gap-2 pb-2 border-b border-white/8">
        <Sword className="h-4 w-4 text-emerald-300" />
        <div>
          <div className="font-semibold">Turn Tracker</div>
          <div className="text-white/55">
            {encounter.status === "active"
              ? `Round ${encounter.round}`
              : "Preparing"}
          </div>
        </div>

        <button
          type="button"
          className="ml-auto rounded px-2 py-1 border border-white/20 bg-white/10 hover:bg-white/15"
          onClick={() => setIsCollapsed((prev) => !prev)}
          title={isCollapsed ? "Expand" : "Collapse"}
        >
          {isCollapsed ? (
            <ChevronDown className="h-3.5 w-3.5" />
          ) : (
            <ChevronUp className="h-3.5 w-3.5" />
          )}
        </button>
      </div>

      {!isCollapsed && (
        <div className="mt-2 flex flex-col gap-2">
          {encounter.status === "preparing" &&
            encounterParticipants.length === 0 && (
              <div className="text-white/55">Add combatants to begin.</div>
            )}

          {encounter.status === "preparing" && (
            <div className="flex items-center gap-2">
              <select
                value={selectedParticipantId}
                onChange={(event) =>
                  setSelectedParticipantId(event.target.value)
                }
                className="rounded bg-black/30 border border-white/15 px-2 py-1"
              >
                <option value="">Add combatant...</option>
                {availableParticipants.map((entry) => (
                  <option key={entry.id} value={entry.id}>
                    {entry.name ?? entry.id}
                  </option>
                ))}
              </select>

              <button
                type="button"
                className="rounded px-2 py-1 border border-white/20 bg-white/10 hover:bg-white/15"
                onClick={() => {
                  if (!selectedParticipantId || !encounter.id) return;
                  addToEncounter(doc, encounter.id, selectedParticipantId, 0);
                  setSelectedParticipantId("");
                }}
              >
                <Plus className="inline-block h-3.5 w-3.5 mr-1" />
                Add
              </button>
            </div>
          )}

          <div className="space-y-1 max-h-[38vh] overflow-y-auto pr-1">
            {encounterParticipants.map((entry: EncounterTurnEntry, index) => (
              <div
                key={entry.blockId}
                className="flex items-center gap-2 rounded border border-white/10 px-2 py-1 bg-white/[0.05]"
              >
                <div className="w-5 text-center text-white/65">{index + 1}</div>
                <div className="flex-1 truncate">
                  {entry.name || entry.blockId}
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-white/55 text-[10px]">Init</span>
                  <input
                    type="number"
                    defaultValue={Math.round(toFiniteNumber(entry.initiative))}
                    className="w-16 rounded bg-black/30 border border-white/20 px-1.5 py-0.5"
                    onBlur={(event) => {
                      if (!encounter) return;
                      const next = Number(event.currentTarget.value);
                      if (!Number.isFinite(next)) return;
                      addToEncounter(doc, encounter.id, entry.blockId, next);
                    }}
                  />
                  <button
                    type="button"
                    className="rounded px-2 py-1 border border-white/20 bg-white/10 hover:bg-white/15"
                    onClick={() => {
                      if (!encounter) return;
                      removeFromEncounter(doc, encounter.id, entry.blockId);
                    }}
                    title="Remove"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="flex items-center gap-2 mt-1">
            {encounter.status === "preparing" && (
              <>
                <button
                  type="button"
                  className="rounded px-2 py-1 border border-white/20 bg-white/10 hover:bg-white/15"
                  onClick={() => {
                    if (!encounter) return;
                    encounter.turnOrder.forEach((entry) => {
                      const roll = 1 + Math.floor(Math.random() * 20);
                      addToEncounter(doc, encounter.id, entry.blockId, roll);
                    });
                  }}
                >
                  <Crosshair className="inline-block h-3.5 w-3.5 mr-1" />
                  Roll Initiative
                </button>

                <button
                  type="button"
                  className="rounded px-2 py-1 border border-white/20 bg-emerald-600/25 text-emerald-100 hover:bg-emerald-500/30"
                  onClick={() => {
                    if (!encounter) return;
                    startEncounter(doc, encounter.id);
                  }}
                >
                  <Play className="inline-block h-3.5 w-3.5 mr-1" />
                  Start
                </button>
              </>
            )}

            {encounter.status === "active" && (
              <>
                <button
                  type="button"
                  className="rounded px-2 py-1 border border-white/20 bg-white/10 hover:bg-white/15"
                  onClick={() => {
                    if (!encounter) return;
                    nextTurn(doc, encounter.id);
                  }}
                >
                  <Repeat className="inline-block h-3.5 w-3.5 mr-1" />
                  Next Turn
                </button>

                <button
                  type="button"
                  className="rounded px-2 py-1 border border-white/20 bg-white/10 hover:bg-white/15"
                  onClick={() => {
                    if (!encounter) return;
                    endEncounter(doc, encounter.id);
                  }}
                >
                  <SlidersHorizontal className="inline-block h-3.5 w-3.5 mr-1" />
                  End Encounter
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
